import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { useModels } from '../../contexts/ModelContext';
import { getTimeAgo } from '../../utils/formatUtils';
import { captureAnalyticsEvent } from '../../analytics/posthog';
import { buildChatSearchIndexUpdateFromMessages } from '../../utils/chatSearchIndex';

const DEFAULT_ATTACHMENT_NAMES = {
  image: 'Image',
  pdf: 'PDF Document',
  audio: 'Audio Recording',
  video: 'Video Recording',
};

const areMessagesShallowEqual = (previous, next) => {
  if (previous === next) return true;
  if (!previous || !next) return false;
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);
  if (previousKeys.length !== nextKeys.length) return false;
  return previousKeys.every((key) => Object.is(previous[key], next[key]));
};

const buildLegacyAttachments = (messageId, data) => {
  const attachments = [];

  if (data.imageUrl) {
    attachments.push({
      id: `${messageId}-legacy-image`,
      type: 'image',
      name: data.imageName || DEFAULT_ATTACHMENT_NAMES.image,
      url: data.imageUrl,
      fileUri: data.fileUri || null,
      mimeType: data.mimeType || null,
    });
  }

  if (data.pdfUrl) {
    attachments.push({
      id: `${messageId}-legacy-pdf`,
      type: 'pdf',
      name: data.pdfName || DEFAULT_ATTACHMENT_NAMES.pdf,
      url: data.pdfUrl,
      fileUri: data.fileUri || null,
      mimeType: data.mimeType || 'application/pdf',
    });
  }

  if (data.audioUrl) {
    attachments.push({
      id: `${messageId}-legacy-audio`,
      type: 'audio',
      name: data.audioName || DEFAULT_ATTACHMENT_NAMES.audio,
      url: data.audioUrl,
      fileUri: data.fileUri || null,
      mimeType: data.mimeType || 'audio/mpeg',
    });
  }

  if (data.videoUrl) {
    attachments.push({
      id: `${messageId}-legacy-video`,
      type: 'video',
      name: data.videoName || DEFAULT_ATTACHMENT_NAMES.video,
      url: data.videoUrl,
      fileUri: data.fileUri || null,
      mimeType: data.mimeType || 'video/mp4',
    });
  }

  return attachments;
};

const normalizeAttachments = (messageId, data) => {
  const storedAttachments = Array.isArray(data.attachments) ? data.attachments : [];

  if (storedAttachments.length > 0) {
    return storedAttachments.map((attachment, index) => ({
      id: attachment.id || `${messageId}-attachment-${index}`,
      type: attachment.type || 'file',
      name: attachment.name || DEFAULT_ATTACHMENT_NAMES[attachment.type] || 'Attachment',
      url: attachment.url || null,
      storagePath: attachment.storagePath || null,
      fileUri: attachment.fileUri || null,
      mimeType: attachment.mimeType || null,
      size: attachment.size ?? null,
    }));
  }

  return buildLegacyAttachments(messageId, data);
};

export const useChatState = () => {
  const { models, defaultModel, getModelByName, getModelByModelString } = useModels();
  const [chatTitle, setChatTitle] = useState('New Chat');
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState({});
  const [secondaryMessages, setSecondaryMessages] = useState({});
  const [isDualMode, setIsDualMode] = useState(false);
  const [secondaryModel, setSecondaryModel] = useState(null);
  const [selectedModel, setSelectedModel] = useState(() => {
    const pinnedModel = localStorage.getItem('pinned-model');
    return pinnedModel || 'gpt-4o-mini';
  });
  const [selectedModelName, setSelectedModelName] = useState(() => {
    const pinnedModel = localStorage.getItem('pinned-model');
    if (pinnedModel) {
      const modelObj = null; // resolved after models load
      return modelObj?.name || 'Deprecated Model';
    }
    return 'GPT 4o mini';
  });
  const [messageLimit, setMessageLimit] = useState(0);
  const [reasoningStream, setReasoningStream] = useState('');
  const [inputText, setInputText] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null); // { chatId, messageId, text }

  const navigate = useNavigate();

  const getNewChatModelSelection = useCallback(() => {
    const pinnedModelString = localStorage.getItem('pinned-model');
    const fallbackModel = defaultModel || models[0] || null;

    if (!pinnedModelString) {
      return {
        modelString: fallbackModel?.modelString || 'gpt-4o-mini',
        modelName: fallbackModel?.name || 'Deprecated Model',
        modelMessageLimit: fallbackModel?.messageLimit || 0,
      };
    }

    const pinnedModelObject =
      getModelByModelString(pinnedModelString) ||
      getModelByName(pinnedModelString);

    if (pinnedModelObject) {
      return {
        modelString: pinnedModelObject.modelString,
        modelName: pinnedModelObject.name,
        modelMessageLimit: pinnedModelObject.messageLimit || 0,
      };
    }

    return {
      modelString: fallbackModel?.modelString || 'gpt-4o-mini',
      modelName: fallbackModel?.name || 'Deprecated Model',
      modelMessageLimit: fallbackModel?.messageLimit || 0,
    };
  }, [defaultModel, models, getModelByModelString, getModelByName]);

  // When models load, ensure defaults align with Firestore catalog
  useEffect(() => {
    if (!selectedChat && defaultModel) {
      setSelectedModel((prev) => {
        if (!prev || prev === 'gpt-4o-mini') return defaultModel.modelString;
        return prev;
      });
      setSelectedModelName((prev) => {
        if (!prev || prev === 'GPT 4o mini' || prev === 'Deprecated Model') {
          return defaultModel.name;
        }
        return prev;
      });
      setMessageLimit(defaultModel.messageLimit || 0);
    }
  }, [defaultModel, selectedChat]);

  // Listen to chats from Firebase
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/');
      return;
    }
    const chatsRef = collection(firestore, 'chats');
    const q = query(
      chatsRef,
      where('owner', '==', user.uid),
      orderBy('lastMessageSent', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fetchedChats = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const lastMessageSent = data.lastMessageSent ? data.lastMessageSent.toDate() : new Date();
        return {
          id: doc.id,
          ...data,
          lastMessageTimeAgo: getTimeAgo(lastMessageSent)
        };
      });
      setChats(fetchedChats);
    }, (error) => {
      console.error("Error fetching chats:", error);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Update model when chat is selected
  useEffect(() => {
    if (selectedChat) {
      const chatModel =
        getModelByName(selectedChat.model) ||
        getModelByModelString(selectedChat.model);

      if (chatModel) {
        setSelectedModel(chatModel.modelString);
        setSelectedModelName(chatModel.name);
        setMessageLimit(chatModel.messageLimit);
      } else {
        // Model no longer exists, use deprecated fallback
        const fallback = defaultModel || models[0];
        setSelectedModel(fallback?.modelString || 'DeprecatedModel');
        setSelectedModelName(fallback?.name || 'DeprecatedModel');
        setMessageLimit(fallback?.messageLimit || 10);
      }
    } else {
      const {
        modelString: newChatModelString,
        modelName: newChatModelName,
        modelMessageLimit: newChatMessageLimit,
      } = getNewChatModelSelection();
      setSelectedModel(newChatModelString);
      setSelectedModelName(newChatModelName);
      setMessageLimit(newChatMessageLimit);
    }
  }, [selectedChat, defaultModel, getModelByModelString, getModelByName, models, getNewChatModelSelection]);

  const handleReasoningStreamUpdate = useCallback((partialReasoning) => {
    setReasoningStream(partialReasoning);
  }, []);

  const handleChatSelect = useCallback(async (chatId, isMobile, setIsSidebarOpen) => {
    const selected = chats.find(chat => chat.id === chatId);
    if (!selected) return;

    captureAnalyticsEvent('chat_selected', {
      chat_id: selected.id,
      chat_topic: selected.topic || 'Untitled Chat',
      model_name: selected.model || null,
      is_mobile: Boolean(isMobile),
    });

    setSelectedChat(selected);
    setChatTitle(selected.topic || 'New Chat');
    setShowSettings(false);
    setEditingMessage(null);
    setInputText('');
    setIsLoadingMessages(true);
    
    // Check if this is a multi-model chat by reading from the database
    try {
      const chatDoc = await getDoc(doc(firestore, 'chats', chatId));
      if (chatDoc.exists()) {
        const chatData = chatDoc.data();
        const isMultiModelChat = chatData.multi_model || false;
        
        if (isMultiModelChat && !isDualMode) {
          setIsDualMode(true);
        } else if (!isMultiModelChat && isDualMode) {
          setIsDualMode(false);
          setSecondaryModel(null);
        }
      }
    } catch (error) {
      console.error('Error checking chat multi_model field:', error);
      // Fallback to current message-based logic
      setIsDualMode(false);
      setSecondaryModel(null);
    }
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    // Fetch messages for the selected chat from Firebase
    const messagesRef = collection(firestore, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const allMessages = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const normalizedAttachments = normalizeAttachments(data.id, data);

        return {
          id: data.id,
          text: data.text,
          role: data.role,
          attachments: normalizedAttachments,
          reasoning_text: data.reasoning_text || null,
          reasoning_is_summary: data.reasoning_is_summary || false,
          reasoning_time: data.reasoning_time || null,
          citations: data.citations || null,
          webSearchCalls: data.webSearchCalls || null,
          webSearchStatus: data.webSearchStatus || null,
          createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
          // Dual mode fields
          modelUsed: data.modelUsed || null,
          isDualMode: data.isDualMode || false,
          dualModeType: data.dualModeType || null,
          openaiResponseId: data.openaiResponseId || null,
          toolName: data.toolName || null,
          toolError: data.toolError || false,
        };
      });

      // Separate messages based on dual mode type
      const primaryMessages = [];
      const secondaryMessages = [];
      let hasDualModeMessages = false;
      let primaryModelUsed = null;
      let secondaryModelUsed = null;

      const resolveModelString = (modelValue) => {
        if (!modelValue) return null;
        const modelByString = getModelByModelString(modelValue);
        if (modelByString) return modelByString.modelString;
        const modelByName = getModelByName(modelValue);
        return modelByName?.modelString || null;
      };

      allMessages.forEach(message => {
        if (message.role === 'user') {
          // User messages go to both arrays
          primaryMessages.push(message);
          secondaryMessages.push(message);
          if (message.isDualMode) {
            hasDualModeMessages = true;
          }
        } else if (message.role === 'assistant') {
          if (message.isDualMode) {
            hasDualModeMessages = true;
            if (message.dualModeType === 'primary') {
              primaryMessages.push(message);
              if (!primaryModelUsed) primaryModelUsed = resolveModelString(message.modelUsed);
            } else if (message.dualModeType === 'secondary') {
              secondaryMessages.push(message);
              if (!secondaryModelUsed) secondaryModelUsed = resolveModelString(message.modelUsed);
            }
          } else {
            // Non-dual mode messages go to primary array only
            primaryMessages.push(message);
          }
        }
      });

      // Auto-enable/disable dual mode based on message content (fallback for old chats)
      // Only use message-based detection if database field check hasn't already set dual mode
      if (hasDualModeMessages && !isDualMode) {
        setIsDualMode(true);
        if (secondaryModelUsed && secondaryModelUsed !== selectedModel) {
          setSecondaryModel(secondaryModelUsed);
        }
      }
      
      // Set secondary model if we're in dual mode but don't have one set
      if (isDualMode && !secondaryModel && secondaryModelUsed) {
        setSecondaryModel(secondaryModelUsed);
      }

      setMessages((prevMessages) => {
        const prevChatMessages = prevMessages[chatId] || [];
        // Preserve any local in-flight messages (pending or streaming) that
        // haven't been written to Firestore yet. These can get dropped when
        // the snapshot arrives before the assistant response is stored,
        // which makes the UI show only the typing indicator until the user
        // reloads the chat.
        const optimisticMessages = prevChatMessages.filter(
          (msg) =>
            (msg.pending || msg.streaming) &&
            !primaryMessages.some((serverMsg) => serverMsg.id === msg.id)
        );

        return {
          ...prevMessages,
          [chatId]: [...primaryMessages, ...optimisticMessages],
        };
      });

      setSecondaryMessages((prevMessages) => {
        const prevChatMessages = prevMessages[chatId] || [];
        const optimisticMessages = prevChatMessages.filter(
          (msg) =>
            (msg.pending || msg.streaming) &&
            !secondaryMessages.some((serverMsg) => serverMsg.id === msg.id)
        );

        return {
          ...prevMessages,
          [chatId]: [...secondaryMessages, ...optimisticMessages],
        };
      });

      // Turn off loading state once messages are loaded
      setIsLoadingMessages(false);
    });

    return unsubscribe;
  }, [chats, isDualMode, selectedModel, secondaryModel, getModelByModelString, getModelByName]);

  const handleNewChat = useCallback((isMobile, setIsSidebarOpen) => {
    setSelectedChat(null);
    const {
      modelString: newChatModelString,
      modelName: newChatModelName,
      modelMessageLimit: newChatMessageLimit,
    } = getNewChatModelSelection();

    captureAnalyticsEvent('new_chat_started', {
      default_model_string: newChatModelString,
      default_model_name: newChatModelName,
      is_mobile: Boolean(isMobile),
    });
    
    setSelectedModel(newChatModelString);
    setSelectedModelName(newChatModelName);
    setMessageLimit(newChatMessageLimit);
    setMessages({ new: [] });
    setSecondaryMessages({ new: [] });
    setChatTitle('New Chat');
    setShowSettings(false);
    setIsDualMode(false);
    setSecondaryModel(null);
    setEditingMessage(null);
    setInputText('');
    setIsLoadingMessages(false);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [getNewChatModelSelection]);

  const updateChatTitle = useCallback((newTitle) => {
    setChatTitle(newTitle);
  }, []);

  const handleModelChange = useCallback((selectedModelString) => {
    const selectedModelObject =
      getModelByModelString(selectedModelString) ||
      getModelByName(selectedModelString);

    captureAnalyticsEvent('model_selected', {
      model_string: selectedModelString,
      model_name: selectedModelObject?.name || 'Deprecated Model',
      source: selectedChat ? 'existing_chat' : 'new_chat',
    });

    setSelectedModel(selectedModelString);
    setSelectedModelName(selectedModelObject?.name || 'Deprecated Model');
    setMessageLimit(selectedModelObject?.messageLimit || 0);
  }, [getModelByModelString, getModelByName, selectedChat]);

  const handlePinnedModelChange = useCallback((pinnedModelString) => {
    captureAnalyticsEvent('default_model_updated', {
      pinned_model_string: pinnedModelString || null,
    });

    // When a model is pinned/unpinned, optionally update current selection for new chats
    if (!selectedChat && pinnedModelString) {
      const pinnedModelObject = getModelByModelString(pinnedModelString);
      if (pinnedModelObject) {
        setSelectedModel(pinnedModelString);
        setSelectedModelName(pinnedModelObject.name);
        setMessageLimit(pinnedModelObject.messageLimit);
      }
    }
  }, [selectedChat, getModelByModelString]);

  const handleMessageSent = useCallback(async (message, chatId) => {
    setMessages((prevMessages) => {
      const currentMessages = prevMessages[chatId] || [];

      // Find the index of the message with the same ID
      const existingIndex = currentMessages.findIndex(msg => msg.id === message.id);

      if (existingIndex !== -1) {
        // If the message exists, update it without creating a new array if nothing changed
        const existingMessage = currentMessages[existingIndex];
        
        // Check if anything actually changed before updating
        if (areMessagesShallowEqual(existingMessage, message)) {
          return prevMessages; // No changes, return the same state
        }
        
        // Something changed, create a new array with the updated message
        const updatedMessages = [...currentMessages];
        updatedMessages[existingIndex] = message;
        return {
          ...prevMessages,
          [chatId]: updatedMessages,
        };
      } else {
        // If it's a new message, append it
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (lastMessage && lastMessage.text === message.text && lastMessage.role === message.role) {
          // It's a duplicate, don't add it
          return prevMessages;
        }
        // It's a new unique message, append it
        return {
          ...prevMessages,
          [chatId]: [...currentMessages, message],
        };
      }
    });

    // If this is a new chat, update the selectedChat state and chats list
    if (!selectedChat || selectedChat.id !== chatId) {
      const chatDoc = await getDoc(doc(firestore, 'chats', chatId));
      if (chatDoc.exists()) {
        const chatData = chatDoc.data();
        const lastMessageSent = chatData.lastMessageSent ? chatData.lastMessageSent.toDate() : new Date();
        const newChat = {
          id: chatId,
          ...chatData,
          lastMessageTimeAgo: getTimeAgo(lastMessageSent),
        };
        setSelectedChat(newChat);
        setChats((prevChats) => [newChat, ...prevChats.filter(chat => chat.id !== chatId)]);
        
        // Check if this is a multi-model chat and set dual mode accordingly
        const isMultiModelChat = chatData.multi_model || false;
        if (isMultiModelChat && !isDualMode) {
          setIsDualMode(true);
        } else if (!isMultiModelChat && isDualMode) {
          setIsDualMode(false);
          setSecondaryModel(null);
        }
      }
    }
  }, [selectedChat, isDualMode]);

  const handleSecondaryMessageSent = useCallback(async (message, chatId) => {
    setSecondaryMessages((prevMessages) => {
      const currentMessages = prevMessages[chatId] || [];

      // Find the index of the message with the same ID
      const existingIndex = currentMessages.findIndex(msg => msg.id === message.id);

      if (existingIndex !== -1) {
        // If the message exists, update it without creating a new array if nothing changed
        const existingMessage = currentMessages[existingIndex];
        
        // Check if anything actually changed before updating
        if (areMessagesShallowEqual(existingMessage, message)) {
          return prevMessages; // No changes, return the same state
        }
        
        // Something changed, create a new array with the updated message
        const updatedMessages = [...currentMessages];
        updatedMessages[existingIndex] = message;
        return {
          ...prevMessages,
          [chatId]: updatedMessages,
        };
      } else {
        // If it's a new message, append it
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (lastMessage && lastMessage.text === message.text && lastMessage.role === message.role) {
          // It's a duplicate, don't add it
          return prevMessages;
        }
        // It's a new unique message, append it
        return {
          ...prevMessages,
          [chatId]: [...currentMessages, message],
        };
      }
    });
  }, []);

  const handleEditAndTruncate = useCallback(async ({ chatId, messageId, newText }) => {
    if (!chatId || !messageId) {
      throw new Error('Missing chat or message id');
    }

    const primaryMessages = messages[chatId] || [];
    const secondaryMessagesForChat = secondaryMessages[chatId] || [];

    const targetIndex = primaryMessages.findIndex((msg) => msg.id === messageId && msg.role === 'user');
    if (targetIndex === -1) {
      throw new Error('Message not found or not editable');
    }

    const secondaryIndex = secondaryMessagesForChat.findIndex((msg) => msg.id === messageId);

    const updatedMessage = {
      ...primaryMessages[targetIndex],
      text: newText,
    };

    const truncatedPrimary = [...primaryMessages.slice(0, targetIndex), updatedMessage];
    const truncatedSecondary =
      secondaryIndex !== -1
        ? [...secondaryMessagesForChat.slice(0, secondaryIndex), updatedMessage]
        : secondaryMessagesForChat;

    const idsToDelete = new Set();
    primaryMessages.slice(targetIndex + 1).forEach((msg) => idsToDelete.add(msg.id));
    if (secondaryIndex !== -1) {
      secondaryMessagesForChat.slice(secondaryIndex + 1).forEach((msg) => idsToDelete.add(msg.id));
    }

    // Optimistically update local state
    setMessages((prev) => ({
      ...prev,
      [chatId]: truncatedPrimary,
    }));
    setSecondaryMessages((prev) => ({
      ...prev,
      [chatId]: truncatedSecondary,
    }));

    const batch = writeBatch(firestore);

    batch.update(doc(firestore, 'chats', chatId, 'messages', messageId), {
      text: newText,
      updatedAt: serverTimestamp(),
    });

    idsToDelete.forEach((id) => {
      batch.delete(doc(firestore, 'chats', chatId, 'messages', id));
    });

    await batch.commit();

    const chatForIndex =
      chats.find((chat) => chat.id === chatId) ||
      (selectedChat?.id === chatId ? selectedChat : {});
    await updateDoc(
      doc(firestore, 'chats', chatId),
      buildChatSearchIndexUpdateFromMessages({
        chat: chatForIndex,
        messages: truncatedPrimary,
      })
    );

    return {
      updatedMessage,
      truncatedPrimary,
      truncatedSecondary,
    };
  }, [chats, messages, secondaryMessages, selectedChat]);

  const handleSettingsClick = useCallback((isMobile, setIsSidebarOpen) => {
    setEditingMessage(null);
    setShowSettings(true);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, []);

  const handleBackToChat = useCallback(() => {
    setShowSettings(false);
  }, []);

  const handleQuestionClick = useCallback((question) => {
    setInputText(question);
  }, []);

  const startEditingMessage = useCallback((chatId, message) => {
    if (!chatId || !message?.id) return;
    setEditingMessage({
      chatId,
      messageId: message.id,
      text: message.text || '',
    });
  }, []);

  const cancelEditingMessage = useCallback(() => {
    setEditingMessage(null);
  }, []);

  return {
    // State
    chatTitle,
    chats,
    selectedChat,
    messages,
    secondaryMessages,
    isDualMode,
    secondaryModel,
    selectedModel,
    selectedModelName,
    messageLimit,
    reasoningStream,
    inputText,
    showSettings,
    isLoadingMessages,
    
    // Setters
    setIsDualMode,
    setSecondaryModel,
    setInputText,
    
    // Handlers
    handleReasoningStreamUpdate,
    handleChatSelect,
    handleNewChat,
    updateChatTitle,
    handleModelChange,
    handlePinnedModelChange,
    handleMessageSent,
    handleSecondaryMessageSent,
    handleSettingsClick,
    handleBackToChat,
    handleQuestionClick,
    startEditingMessage,
    cancelEditingMessage,
    editingMessage,
    handleEditAndTruncate
  };
};
